import { useAppSelector } from 'store/hooks';
import { QueryStatus } from '@reduxjs/toolkit/query';
import {
  DomainStatus,
  PaidStatus,
  Project,
  ReportInfo,
  UserInfoResponse,
} from 'store/api/apiTypes';
import { userApiSlice } from 'store/api/userApiSlice';

const isStandalone = process.env.REACT_APP_TYPE === 'standalone';

export const useCachedUserInfo = (): {
  isLoading: boolean;
  inactiveDomains: string[] | undefined;
  activeDomains: Project[] | undefined;
  allDomains: Project[];
  lastReport?: ReportInfo;
  userInfo?: UserInfoResponse;
  testingB: boolean;
  visionRequested: boolean;
  totalCount: number;
  unassignedCount: number;
} => {
  const selectedProject = useAppSelector(
    state => state.project.selectedProject
  );
  const userInfoState = useAppSelector(
    userApiSlice.endpoints.getUserInfo.select()
  );
  const inactiveDomains = userInfoState.data?.inactiveDomains;
  const activeDomains = userInfoState.data?.activeDomains;
  const allDomains: Project[] = [
    ...(activeDomains ?? []),
    ...(!isStandalone
      ? (inactiveDomains ?? []).map(
          (domain): Project => ({
            domain,
            paid: PaidStatus.UNPAID,
            data: {
              id: '',
              domain_state: DomainStatus.NO_LICENSE,
              assign_date: null,
              userID: '',
              domainname: domain,
              paid: PaidStatus.UNPAID,
              date: '',
              license_id: null,
              vision_mode_access_requested: null,
              vision_mode_access_granted: null,
            },
            reportsAvailable: false,
          })
        )
      : []),
  ];
  const abTesting = userInfoState.data?.ab_testing;

  // Trial calculations: unified daysLeft and isTrialExpired (midnight-based, ignoring hours/minutes)
  const lastReport = selectedProject?.reports?.[0];

  const isLoading =
    userInfoState?.status === QueryStatus.pending ||
    userInfoState?.status === QueryStatus.uninitialized;

  const newUser = new Date('2025-12-06') < new Date(lastReport?.date ?? '');
  const testingB = abTesting === 'B' && newUser;
  const visionRequested = !!selectedProject?.data?.vision_mode_access_requested;
  const unassignedCount = allDomains.filter(
    d => d.data?.domain_state !== DomainStatus.PAID
  ).length;

  return {
    userInfo: userInfoState.data,
    visionRequested,
    lastReport,
    isLoading,
    inactiveDomains,
    activeDomains,
    allDomains,
    testingB,
    totalCount: allDomains.length,
    unassignedCount,
  };
};
